import { Download, FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from 'vnetwork-ui'
import { TenderChapter } from '../../types'

interface TenderDocumentsProps {
    documents: TenderChapter[]
    onDownload: (file_id: string) => void
    onDownloadAll: () => void
    isDownloading: boolean
}

const TenderDocuments = ({
    documents,
    onDownload,
    onDownloadAll,
    isDownloading,
}: TenderDocumentsProps) => {
    const { t } = useTranslation('tenders')
    const hasDocuments = documents && documents.length > 0

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/50">
                <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {t('documents')}
                    </h3>
                    <span className="px-2.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-medium text-gray-600 dark:text-gray-300">
                        {hasDocuments
                            ? documents.reduce(
                                  (acc, chapter) =>
                                      acc + chapter.documents.length,
                                  0
                              )
                            : 0}
                    </span>
                </div>
                <Button
                    onClick={onDownloadAll}
                    startIcon={<Download className="w-4 h-4" />}
                    disabled={!hasDocuments}
                >
                    {t('download_all_document')}
                </Button>
            </div>
            <div className="p-6 flex flex-col gap-8">
                {hasDocuments ? (
                    documents.map((chapter, cIndex) => (
                        <div key={cIndex} className="flex flex-col gap-4">
                            <div className="flex items-center gap-2 pb-2 border-b border-gray-100 dark:border-gray-800">
                                <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                    {chapter.chapter_name ||
                                        chapter.chapterName ||
                                        chapter.ChapterName}
                                </h4>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {chapter.documents.map((doc, index) => (
                                    <div
                                        key={doc.file_id || index}
                                        className="group flex items-start justify-between p-3.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-indigo-300 dark:hover:border-indigo-700 hover:shadow-md transition-all duration-200"
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform duration-200">
                                                <FileText className="w-5 h-5" />
                                            </div>
                                            <span
                                                className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate line-clamp-2"
                                                title={doc.title}
                                            >
                                                {doc.title}
                                            </span>
                                        </div>
                                        <Button
                                            variant="text"
                                            className="!min-w-0 !p-2 !rounded-full hover:!bg-indigo-50 dark:hover:!bg-indigo-900/30 !text-gray-400 hover:!text-indigo-600 dark:hover:!text-indigo-400 transition-colors"
                                            onClick={() =>
                                                onDownload(doc.file_id)
                                            }
                                            disabled={isDownloading}
                                            title={t('download')}
                                        >
                                            <Download className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-gray-400 dark:text-gray-500">
                        <span className="text-sm font-medium">
                            {t('no_documents')}
                        </span>
                    </div>
                )}
            </div>
        </div>
    )
}

export default TenderDocuments
